import { useScreenSize } from '@hooks/useScreenSize';
import { Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import ToggleButton, { toggleButtonClasses } from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { ColumnState } from 'ag-grid-enterprise';
import { useCallback, useEffect, useState } from 'react';
import { usePricesSettings } from '../PricesContext';
import { BidAskType } from '../types';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    border: '2px solid transparent !important',
    backgroundColor: `${alpha(theme.palette.primary.main, 0.1)}!important`,
    color: alpha(theme.palette.text.secondary, 0.5),
    textTransform: 'none',
    '&:not(:first-of-type)': {
      marginLeft: '3px !important',
    },
    '&:first-of-type': {
      borderRadius: '0px',
    },
    '&:last-of-type': {
      borderRadius: '0px',
    },
    [`&.${toggleButtonClasses.selected}`]: {
      color: theme.palette.text.primary,
      backgroundColor: `${alpha(theme.palette.primary.main, 0.5)}!important`,
    },
    [`&.${toggleButtonClasses.disabled}`]: {
      border: 0,
    },
  },
}));

export const PriceGridControls = () => {
  const { gridRef, selectedBidAskColumns, setNewBidAskColumns, toggleIsShowCurrencyUnit, isShowCurrencyUnit } = usePricesSettings();
  const { isMobile } = useScreenSize();
  const [activeColumns, setActiveColumns] = useState<BidAskType[]>(selectedBidAskColumns);

  const onActiveColumnsChange = useCallback((_: React.MouseEvent<HTMLElement>, newActiveColumns: BidAskType[]) => {
    if (!gridRef?.current) {
      return;
    }

    const gridApi = gridRef.current.getGridApi();
    const columnsNewState = gridApi?.getColumnState().reduce<ColumnState[]>((acc, { colId }) => {
      const columnType = colId.split('-')[1];
      const isColumnTypeActive = newActiveColumns.find(c => c === columnType) || colId.includes('mid') || colId === 'tenorId';

      acc.push({
        colId,
        hide: !isColumnTypeActive,
      });

      return acc;
    }, []);

    gridApi?.applyColumnState({ state: columnsNewState });

    setActiveColumns(newActiveColumns);
    setNewBidAskColumns(newActiveColumns);
  }, []);

  useEffect(() => {
    setActiveColumns(selectedBidAskColumns);
  }, [selectedBidAskColumns]);

  return (
    <>
      <StyledToggleButtonGroup value={activeColumns} onChange={onActiveColumnsChange}>
        {Object.keys(BidAskType).map(type => (
          <ToggleButton
            value={BidAskType[type]}
            key={type}
            aria-label={type}
            size={isMobile ? 'small' : 'medium'}
            sx={{ padding: 0.5, minWidth: '50px' }}
          >
            <Typography fontSize="11px">{`${BidAskType[type].toLocaleUpperCase()}`}</Typography>
          </ToggleButton>
        ))}
      </StyledToggleButtonGroup>
      <ToggleButton
        value="currency-unit"
        selected={isShowCurrencyUnit}
        onClick={toggleIsShowCurrencyUnit}
        aria-label="Toggle currency unit header"
        size={isMobile ? 'small' : 'medium'}
        sx={{ padding: 0.5, minWidth: '50px', ml: 1, borderRadius: '0px' }}
      >
        <Typography fontSize="11px">{'UNIT'}</Typography>
      </ToggleButton>
    </>
  );
};
